// src/pages/hospitality/HospitalityCategory.jsx
import { useState, useEffect, useRef, useContext } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getDistanceFromLatLonInKm } from "../../utils/distance";
import ListFacility from "./ListFacility";
import "../../styles/HospitalityCategory.css";
import { getTranslation } from "../../utils/translations";

export default function HospitalityCategory() {
  const { category } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const t = getTranslation(lang);
  const BACKEND_URL = `${process.env.REACT_APP_API_URL}`;

  const providersRef = useRef(null);

  // ---------------- Auto-scroll first provider card (mobile) ----------------
  useEffect(() => {
    if (providersRef.current && window.innerWidth <= 768) {
      providersRef.current.scrollTo({ left: 16, behavior: "smooth" });
    }
  }, []);

  // ---------------- Styles ----------------
  const blurredStyle = {
    filter: "blur(3px)",
    pointerEvents: "none",
    userSelect: "none",
  };

  // ---------------- State ----------------
  const [providers, setProviders] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [userCoords, setUserCoords] = useState(null);
  const [filters, setFilters] = useState({
    service: "",
    address: "",
    nearMe: false,
    distance: 50,
  });
  const [userRatings, setUserRatings] = useState({});
  const [commentTexts, setCommentTexts] = useState({});

  const [replyTexts, setReplyTexts] = useState({});
  const [openReplyBox, setOpenReplyBox] = useState({});

  const [notice, setNotice] = useState("");

  const showNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 2500);
  };

  // ---------------- Fetch Providers ----------------
  useEffect(() => {
    async function fetchProviders() {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/facilities/${category}?lang=${lang}`
        );
        if (!res.ok) throw new Error("Failed to fetch providers");
        const data = await res.json();
        setProviders(data || []);
      } catch (err) {
        console.error(err);
        setProviders([]);
      }
    }
    fetchProviders();
  }, [category, lang]);


  // ---------------- Fetch Feedback ----------------
  useEffect(() => {
    async function fetchFeedback() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/comments`);
        const data = await res.json();
        setFeedback(data || {});
      } catch (err) {
        console.error(err);
      }
    }
    fetchFeedback();
  }, []);

  // ---------------- Get User Location ----------------
  useEffect(() => {
    if (filters.nearMe && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) =>
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      );
    }
  }, [filters.nearMe]);

  // ---------------- Helpers ----------------
  const getAverageRating = (providerId) => {
    const providerFeedback = feedback[providerId];
    if (!providerFeedback?.comments || !providerFeedback.comments.length)
      return 0;
    const ratings = providerFeedback.comments
      .map((c) => Number(c.rating))
      .filter((r) => !isNaN(r) && r > 0);
    if (!ratings.length) return 0;
    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    return Math.round(avg * 10) / 10;
  };

  const handleStarClick = async (providerId, rating) => {
    if (!token || !user) return showNotice(t.hospitality_category.login_to_rate);
    try {
      const existingComment =
        commentTexts[providerId]?.trim() !== ""
          ? commentTexts[providerId]
          : feedback[providerId]?.comments?.find((c) => c.userId === user.id)
            ?.comment || "";

      const body = { providerId, rating: Number(rating) };

      const res = await fetch(`${BACKEND_URL}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Failed to save comment");
      const data = await res.json();

      setFeedback((prev) => ({ ...prev, [providerId]: data }));
      setUserRatings((prev) => ({ ...prev, [providerId]: rating }));
    } catch (err) {
      console.error(err);
      showNotice("Failed to save rating/comment.");
    }
  };

  const handleCommentSubmit = async (providerId, commentText) => {
    if (!token || !user) return showNotice(t.hospitality_category.login_to_rate);
    if (!commentText.trim()) return;
    try {
      const body = {
        providerId,
        comment: commentText,
      };
      const res = await fetch(`${BACKEND_URL}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to save comment");
      const data = await res.json();
      setFeedback((prev) => ({ ...prev, [providerId]: data }));
      setCommentTexts((prev) => ({ ...prev, [providerId]: "" }));
      setUserRatings((prev) => ({ ...prev, [providerId]: body.rating }));
    } catch (err) {
      console.error(err);
      alert("Failed to save rating/comment.");
    }
  };


  const handleOwnerReplySubmit = async (commentId, providerId) => {
    if (!token || !user) return showNotice("Please login first");

    const replyText = replyTexts[commentId]?.trim();
    if (!replyText) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/comments/${commentId}/reply`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ownerReply: replyText }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save owner reply");
      }

      setFeedback((prev) => ({
        ...prev,
        [providerId]: data,
      }));

      setReplyTexts((prev) => ({
        ...prev,
        [commentId]: "",
      }));

      setOpenReplyBox((prev) => ({
        ...prev,
        [commentId]: false,
      }));
    } catch (err) {
      console.error(err);
      showNotice(err.message || "Failed to save owner reply");
    }
  };

  // ---------------- Filtered & Sorted ----------------
  const filteredProviders = providers.filter((p) => {
    const serviceMatch =
      !filters.service ||
      p.services?.toLowerCase().includes(filters.service.toLowerCase()) ||
      p.branches?.some((b) =>
        b.services?.toLowerCase().includes(filters.service.toLowerCase())
      );
    const addressMatch =
      !filters.address ||
      p.address?.toLowerCase().includes(filters.address.toLowerCase()) ||
      p.branches?.some((b) =>
        b.address?.toLowerCase().includes(filters.address.toLowerCase())
      );

    let distanceMatch = true;
    if (filters.nearMe && userCoords) {
      const allDistances = [];
      if (p.lat && p.lng)
        allDistances.push(
          getDistanceFromLatLonInKm(userCoords.lat, userCoords.lng, p.lat, p.lng)
        );
      p.branches?.forEach((b) => {
        if (b.lat && b.lng)
          allDistances.push(
            getDistanceFromLatLonInKm(userCoords.lat, userCoords.lng, b.lat, b.lng)
          );
      });
      distanceMatch = allDistances.length ? Math.min(...allDistances) <= filters.distance : true;
    }

    return serviceMatch && addressMatch && distanceMatch;
  });

  const sortedProviders = [...filteredProviders].sort(
    (a, b) => getAverageRating(b.id) - getAverageRating(a.id)
  );

  // ---------------- Utilities ----------------
  const pluralize = (word) => {
    if (!word) return "";
    const lower = word.toLowerCase();
    const uncountables = ["fast food", "news", "events", "information"];
    if (uncountables.includes(lower)) return word;
    if (word.endsWith("s")) return word;
    if (word.endsWith("y")) return word.slice(0, -1) + "ies";
    return word + "s";
  };
  const capitalizeWords = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };


  const getCategoryEmojis = (categoryName) => {
    const normalized = (categoryName || "").toLowerCase();

    if (normalized.includes("cafe")) {
      return ["☕", "🥐", "🍰", "😊", "❤️", "👍", "👌", "✨"];
    }
    if (normalized.includes("restaurant")) {
      return ["🍽️", "🍕", "🍔", "🥗", "😋", "❤️", "👏", "🔥"];
    }
    if (normalized.includes("bar")) {
      return ["🍹", "🍷", "🍺", "🥂", "😎", "🎉", "🔥", "👍"];
    }
    if (normalized.includes("club")) {
      return ["🎶", "💃", "🕺", "🔥", "🎉", "😍", "🥳", "⚡"];
    }
    if (normalized.includes("hotel")) {
      return ["🏨", "🛏️", "🧳", "✨", "😊", "❤️", "👌", "👍"];
    }

    return ["😊", "❤️", "👍", "✨", "😍", "🎉", "🔥", "👏"];
  };

  const appendEmojiToComment = (providerId, emoji) => {
    setCommentTexts((prev) => ({
      ...prev,
      [providerId]: `${prev[providerId] || ""} ${emoji}`.trim(),
    }));
  };

  const appendEmojiToReply = (commentId, emoji) => {
    setReplyTexts((prev) => ({
      ...prev,
      [commentId]: `${prev[commentId] || ""} ${emoji}`.trim(),
    }));
  };


  // ---------------- JSX ----------------
  return (
    <div className="hospitality-page">
      {notice && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#ffffff",
            color: "#111",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {notice}
        </div>
      )}

      <h2
        style={{
          textAlign: "center",
          marginTop: "20px",
          fontSize: "35px",
          fontWeight: "800",
          color: "#0ea5e9",
        }}
      >
        {t.categories?.[category?.toLowerCase()] || capitalizeWords(pluralize(category))}
      </h2>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", color: "#fff", marginBottom: "20px" }}>
        <input
          placeholder={t.hospitality_category.service_placeholder}
          value={filters.service}
          onChange={(e) => setFilters({ ...filters, service: e.target.value })}
        />
        <input
          placeholder={t.hospitality_category.address_placeholder}
          value={filters.address}
          onChange={(e) => setFilters({ ...filters, address: e.target.value })}
        />
        <label>
          <input
            type="checkbox"
            checked={filters.nearMe}
            onChange={(e) => setFilters({ ...filters, nearMe: e.target.checked })}
          />{" "}
          {t.hospitality_category.near_me}
        </label>
        <input
          type="range"
          min="1"
          max="10000"
          value={filters.distance}
          onChange={(e) => setFilters({ ...filters, distance: Number(e.target.value) })}
        />
        <span style={{ color: "#fff" }}>{filters.distance} {t.hospitality_category.distance_km}</span>
      </div>

      {/* Providers List */}
      <div ref={providersRef} className="providers-grid">
        {sortedProviders.map((p) => {
          const avgRating = getAverageRating(p.id);
          const totalReviews = feedback[p.id]?.comments?.length || 0;
          const userRating = userRatings[p.id] || 0;
          const commentText = commentTexts[p.id] || "";

          return (
            <div
              key={p.id} className="provider-card"
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              onClick={(e) => {
                if (p.paid_plan === false) return; // prevent navigation
                if (e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
                  navigate(`/provider/${p.id}`);
                }
              }}
            >
              {/* Overlay Text */}
              {p.paid_plan === false && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "#ff4d4f", // bright red
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontWeight: "bold",
                    textShadow: "0 0 6px rgba(0,0,0,0.8)",
                    pointerEvents: "none",
                    fontSize: "16px",
                    textAlign: "center",
                    padding: "10px",
                    borderRadius: "15px",
                    zIndex: 10,
                  }}
                >
                  🔒 Renew your membership to be visible
                </div>
              )}

              {/* Provider Card */}
              <div style={{ filter: p.paid_plan === false ? "blur(3px)" : "none" }}>
                <h3
                  style={{
                    padding: "8px",
                    borderRadius: "10px",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    background: "linear-gradient(to right, #87cefa, #0ea5e9)",
                    color: "#fff",
                    marginBottom: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Provider Name + Verified Badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontWeight: "700" }}>{p.name}</span>
                    {p.verified === true && p.paid_plan !== false && (
                      <span
                        style={{
                          marginLeft: "8px",
                          backgroundColor: "#228B22",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "15px",
                          padding: "2px 6px",
                          borderRadius: "12px",
                        }}
                      >
                        ✅ Verified
                      </span>
                    )}
                  </div>
                  {/* Hotel Rank Stars */}
                  {/*
                  {p.category.toLowerCase() === "hotels" && p.rank && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-3px",
                        right: "4px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#facc15",
                        whiteSpace: "nowrap",
                        zIndex: 5,
                      }}
                    >
                      {p.rank}
                    </div>
                  )}
                    */}
                </h3>

                {/* Average Rating */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ marginRight: "8px", fontWeight: "500", color: "#fff" }}>
                    Average Rating: {avgRating}
                  </span>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      style={{
                        color: star <= Math.round(avgRating) ? "#facc15" : "#ccc",
                        fontSize: "18px",
                        marginRight: "2px",
                      }}
                    >
                      ★
                    </span>
                  ))}
                  <span style={{ marginLeft: "8px", fontSize: "12px", color: "#fff" }}>
                    ({totalReviews} reviews)
                  </span>
                </div>

                {/* Rate & Comment */}
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
                    <span style={{ color: "#fff" }}>{t.hospitality_category.rate_this_provider}:</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStarClick(p.id, star);
                        }}
                        style={{
                          cursor: "pointer",
                          color: star <= userRating ? "#facc15" : "#ccc",
                          fontSize: "18px",
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <textarea
                    placeholder={t.hospitality_category.write_comment_placeholder}
                    style={{
                      width: "100%",
                      minHeight: "50px",
                      resize: "vertical",
                      marginBottom: "5px",
                      padding: "8px",
                      borderRadius: "10px",
                      border: "1px solid #ccc",
                      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                    }}
                    value={commentText}
                    onChange={(e) => setCommentTexts((prev) => ({ ...prev, [p.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleCommentSubmit(p.id, commentText);
                      }
                    }}
                  />

                  <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px", marginBottom: "4px" }}>
                    {t.hospitality_category.quick_reactions}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginTop: "4px",
                      marginBottom: "6px",
                    }}
                  >
                    {getCategoryEmojis(p.category).map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          appendEmojiToComment(p.id, emoji);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.15)";
                          e.currentTarget.style.background = "rgba(255,255,255,0.14)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                        }}
                        style={{
                          border: "none",
                          background: "rgba(255,255,255,0.08)",
                          borderRadius: "20px",
                          padding: "4px 8px",
                          cursor: "pointer",
                          fontSize: "18px",
                          transition: "transform 0.15s ease, background 0.15s ease",
                        }}
                        title={`Add ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display Comments */}
                {feedback[p.id]?.comments?.map((c, i) => {
                  const isOwnerOfProvider =
                    user &&
                    p.owner_email &&
                    user.email?.trim().toLowerCase() === p.owner_email?.trim().toLowerCase();

                  return (
                    <div
                      key={i}
                      style={{
                        borderTop: "1px solid #334155",
                        paddingTop: "8px",
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#e0e0e0",
                      }}
                    >
                      <div style={{ fontWeight: "600", color: "#fff" }}>
                        {c.userName || "Anonymous"} ({c.rating}
                        <span style={{ color: "#facc15", fontWeight: "bold" }}>★</span>)
                      </div>

                      {c.comment && (
                        <div style={{ margin: "4px 0", lineHeight: "1.4" }}>
                          {c.comment}
                        </div>
                      )}

                      <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                        {new Date(c.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        · {new Date(c.timestamp).toLocaleDateString("en-GB")}
                      </div>

                      {c.ownerReply && (
                        <div
                          style={{
                            marginTop: "8px",
                            marginLeft: "18px",
                            padding: "8px 10px",
                            borderLeft: "3px solid #0ea5e9",
                            background: "rgba(14,165,233,0.08)",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "700",
                              color: "#38bdf8",
                              marginBottom: "4px",
                            }}
                          >
                            {t.hospitality_category.owner_reply}
                          </div>

                          <div style={{ color: "#e2e8f0", lineHeight: "1.45" }}>
                            {c.ownerReply}
                          </div>

                          {c.ownerReplyTimestamp && (
                            <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>
                              {new Date(c.ownerReplyTimestamp).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              · {new Date(c.ownerReplyTimestamp).toLocaleDateString("en-GB")}
                            </div>
                          )}
                        </div>
                      )}

                      {isOwnerOfProvider && (
                        <div style={{ marginTop: "8px", display: "flex", justifyContent: "flex-end" }}>
                          {!openReplyBox[c.id] ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenReplyBox((prev) => ({
                                  ...prev,
                                  [c.id]: true,
                                }));
                                setReplyTexts((prev) => ({
                                  ...prev,
                                  [c.id]: c.ownerReply || "",
                                }));
                              }}
                              style={{
                                padding: "4px 8px",
                                borderRadius: "6px",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "500",
                                fontSize: "12px",
                                background: "#0ea5e9",
                                color: "#fff",
                              }}
                            >
                              {c.ownerReply
                                ? t.hospitality_category.edit_reply
                                : t.hospitality_category.reply}
                            </button>
                          ) : (
                            <div style={{ width: "100%" }}>
                              <textarea
                                value={replyTexts[c.id] || ""}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  setReplyTexts((prev) => ({
                                    ...prev,
                                    [c.id]: e.target.value,
                                  }))
                                }
                                placeholder={t.hospitality_category.write_owner_reply_placeholder}
                                style={{
                                  width: "100%",
                                  minHeight: "70px",
                                  resize: "vertical",
                                  marginTop: "6px",
                                  padding: "8px",
                                  borderRadius: "10px",
                                  border: "1px solid #475569",
                                  background: "#0f172a",
                                  color: "#fff",
                                }}
                              />

                              <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px", marginBottom: "4px" }}>
                                {t.hospitality_category.quick_reactions}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                  marginTop: "6px",
                                  marginBottom: "4px",
                                }}
                              >
                                {getCategoryEmojis(p.category).map((emoji) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      appendEmojiToReply(c.id, emoji);
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = "scale(1.15)";
                                      e.currentTarget.style.background = "rgba(255,255,255,0.14)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "scale(1)";
                                      e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                                    }}
                                    style={{
                                      border: "none",
                                      background: "rgba(255,255,255,0.08)",
                                      borderRadius: "20px",
                                      padding: "4px 8px",
                                      cursor: "pointer",
                                      fontSize: "18px",
                                      transition: "transform 0.15s ease, background 0.15s ease",
                                    }}
                                    title={`Add ${emoji}`}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>

                              <div
                                style={{
                                  marginTop: "6px",
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: "8px",
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenReplyBox((prev) => ({
                                      ...prev,
                                      [c.id]: false,
                                    }));
                                  }}
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: "1px solid #64748b",
                                    cursor: "pointer",
                                    background: "transparent",
                                    color: "#fff",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {t.hospitality_category.cancel}
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOwnerReplySubmit(c.id, p.id);
                                  }}
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "6px",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    fontSize: "12px",
                                    background: "#22c55e",
                                    color: "#fff",
                                  }}
                                >
                                  {t.hospitality_category.send}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          );
        })}
      </div>
      <ListFacility />
      {/* Bottom message for non-logged-in users */}
      {!user && (
        <div
          style={{
            textAlign: "center",
            marginTop: "250px",
            fontSize: "23px",
            color: "#fff",
            fontWeight: "500",
          }}
        >
          {t.hospitality_category.list_facility_question}{" "}
          {t.hospitality_category.list_facility_prompt}{" "}
          <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
            {t.hospitality_category.log_in}
          </span>{" "}
          {t.hospitality_category.or}{" "}
          <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
            {t.hospitality_category.register}
          </span>{" "}
          {t.hospitality_category.to_proceed}
        </div>
      )}
    </div>
  );
}
